import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import { Bold, Italic, List, ListOrdered, Heading2, Link2, Eye, Edit as EditIcon } from "lucide-react";

/**
 * Lightweight Markdown editor — controlled textarea + preview toggle + a small
 * formatting toolbar that inserts markdown around the current selection. No
 * heavy WYSIWYG dependency.
 *
 * Supported syntax (shown in tooltip): **bold**, *italic*, ## heading, lists,
 * [link](url). Renders sanely on AppDetailPage via the shared <MarkdownView>.
 */
const wrapSelection = (textareaEl, before, after = before) => {
  if (!textareaEl) return null;
  const start = textareaEl.selectionStart;
  const end = textareaEl.selectionEnd;
  const value = textareaEl.value;
  const selected = value.slice(start, end);
  const next = value.slice(0, start) + before + selected + after + value.slice(end);
  // Restore caret around the inserted content
  const newPos = start + before.length + selected.length + after.length;
  return { next, caret: newPos };
};

const insertPrefix = (textareaEl, prefix) => {
  if (!textareaEl) return null;
  const start = textareaEl.selectionStart;
  const value = textareaEl.value;
  // Move start to beginning of current line
  const lineStart = value.lastIndexOf("\n", start - 1) + 1;
  const next = value.slice(0, lineStart) + prefix + value.slice(lineStart);
  return { next, caret: start + prefix.length };
};

const MarkdownEditor = ({ value = "", onChange, placeholder, maxLength = 1500, testId, label, height = "min-h-[160px]" }) => {
  const [mode, setMode] = useState("edit"); // "edit" | "preview"
  const textareaRef = React.useRef(null);

  const apply = (mutation) => {
    const ta = textareaRef.current;
    const result = mutation(ta);
    if (!result) return;
    onChange(result.next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(result.caret, result.caret);
    });
  };

  return (
    <div className="border border-[#E2E8F0] rounded-lg overflow-hidden bg-white" data-testid={testId}>
      <div className="flex items-center justify-between gap-2 bg-[#F8FAFC] border-b border-[#E2E8F0] px-2 py-1.5">
        <div className="flex items-center gap-0.5">
          <button type="button" onClick={() => apply((ta) => wrapSelection(ta, "**"))} title="Bold (Ctrl+B)" className="p-1.5 text-[#64748B] hover:text-[#0F172A] hover:bg-white rounded"><Bold size={13} /></button>
          <button type="button" onClick={() => apply((ta) => wrapSelection(ta, "*"))} title="Italic" className="p-1.5 text-[#64748B] hover:text-[#0F172A] hover:bg-white rounded"><Italic size={13} /></button>
          <span className="w-px h-4 bg-[#E2E8F0] mx-1" />
          <button type="button" onClick={() => apply((ta) => insertPrefix(ta, "## "))} title="Heading" className="p-1.5 text-[#64748B] hover:text-[#0F172A] hover:bg-white rounded"><Heading2 size={13} /></button>
          <button type="button" onClick={() => apply((ta) => insertPrefix(ta, "- "))} title="Bulleted list" className="p-1.5 text-[#64748B] hover:text-[#0F172A] hover:bg-white rounded"><List size={13} /></button>
          <button type="button" onClick={() => apply((ta) => insertPrefix(ta, "1. "))} title="Numbered list" className="p-1.5 text-[#64748B] hover:text-[#0F172A] hover:bg-white rounded"><ListOrdered size={13} /></button>
          <button type="button" onClick={() => apply((ta) => wrapSelection(ta, "[", "](https://)"))} title="Link" className="p-1.5 text-[#64748B] hover:text-[#0F172A] hover:bg-white rounded"><Link2 size={13} /></button>
        </div>
        <div className="inline-flex bg-white border border-[#E2E8F0] rounded-full p-0.5">
          <button type="button" onClick={() => setMode("edit")} className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${mode === "edit" ? "bg-[#0F172A] text-white" : "text-[#64748B]"}`} data-testid={testId ? `${testId}-mode-edit` : undefined}>
            <EditIcon size={9} /> Edit
          </button>
          <button type="button" onClick={() => setMode("preview")} className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${mode === "preview" ? "bg-[#0F172A] text-white" : "text-[#64748B]"}`} data-testid={testId ? `${testId}-mode-preview` : undefined}>
            <Eye size={9} /> Preview
          </button>
        </div>
      </div>
      {mode === "edit" ? (
        <textarea
          ref={textareaRef}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          maxLength={maxLength}
          placeholder={placeholder}
          aria-label={label}
          className={`w-full ${height} p-3 outline-none text-sm leading-relaxed font-mono resize-y`}
          data-testid={testId ? `${testId}-textarea` : undefined}
        />
      ) : (
        <div className={`${height} p-4 overflow-auto`} data-testid={testId ? `${testId}-preview` : undefined}>
          {value ? (
            <MarkdownView text={value} />
          ) : (
            <p className="text-xs text-[#94A3B8] italic">(empty — nothing to preview yet)</p>
          )}
        </div>
      )}
      <div className="text-[10px] text-[#94A3B8] px-3 py-1.5 border-t border-[#E2E8F0] bg-[#F8FAFC] flex items-center justify-between">
        <span>**bold** · *italic* · ## heading · - list · [text](url)</span>
        <span className="tabular-nums">{(value || "").length}/{maxLength}</span>
      </div>
    </div>
  );
};

const MD_COMPONENTS = {
  p: ({ node, ...props }) => <p className="mb-3 last:mb-0" {...props} />,
  strong: ({ node, ...props }) => <strong className="font-bold text-[#0F172A]" {...props} />,
  em: ({ node, ...props }) => <em className="italic" {...props} />,
  h1: ({ node, ...props }) => <h1 className="font-heading text-2xl text-[#0F172A] mt-4 mb-2" {...props} />,
  h2: ({ node, ...props }) => <h2 className="font-heading text-xl text-[#0F172A] mt-4 mb-2" {...props} />,
  h3: ({ node, ...props }) => <h3 className="font-heading text-lg text-[#0F172A] mt-3 mb-2" {...props} />,
  ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-3 space-y-1" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-3 space-y-1" {...props} />,
  li: ({ node, ...props }) => <li className="text-[#475569]" {...props} />,
  a: ({ node, ...props }) => <a className="text-[#2563EB] hover:underline" target="_blank" rel="noreferrer noopener nofollow" {...props} />,
  code: ({ node, ...props }) => <code className="bg-[#F1F5F9] text-[#2563EB] px-1 py-0.5 rounded text-xs font-mono" {...props} />,
  blockquote: ({ node, ...props }) => <blockquote className="border-l-2 border-[#2563EB] pl-3 italic text-[#64748B] my-3" {...props} />,
};

/**
 * Renders markdown as React with sensible Tailwind styling.
 * Used both in the editor preview AND on the public AppDetailPage.
 */
export const MarkdownView = ({ text, className = "" }) => {
  if (!text) return null;
  return (
    <div className={`prose-content text-[#0F172A] leading-relaxed ${className}`}>
      <ReactMarkdown components={MD_COMPONENTS} skipHtml>
        {text}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownEditor;
